/**
 * Phase 6c PR 6 — `<CoachCard />`.
 *
 * Two render paths, kept side-by-side for backwards compat:
 *
 *   1. **`message: CoachMessage`** (Phase 6c — preferred). Server-driven
 *      content from the rules engine (or `mockCoachMessages` for the
 *      demo path). Footer is `[ askLink · spacer · rightMeta? ]` per
 *      direction.md §3.5 — alignment fix prevents askLink drift to
 *      centre when `rightMeta` is absent.
 *
 *   2. **`content: CoachCardContent`** (legacy — Phase 1 design preview).
 *      The 5-state map keyed by `CoachState`. Rendered with the older
 *      kicker / quote / 2-CTA layout. Kept until `useQueryState`
 *      migrates to the new `?coach=` trigger kinds.
 *
 * If both are passed, `message` wins. Tests cover both paths.
 */

import type { CSSProperties } from 'react';
import type { CoachCardContent } from '../../fixtures/coach-states';
import type { CoachId, CoachMessage } from '../../lib/coachMessages';
import { COACH_DISPLAY } from '../../lib/coachMessages';
import styles from './CoachCard.module.css';

interface CoachCardProps {
  /** Phase 6c — preferred. Server-driven content. */
  message?: CoachMessage;
  /** Legacy — Phase 1 fixture. Used when `message` is absent. */
  content?: CoachCardContent;
}

const COACH_DOT_CLASS: Record<CoachId, string> = {
  training: styles.coachDotTraining,
  gear: styles.coachDotGear,
  race: styles.coachDotRace,
  recovery: styles.coachDotRecovery,
  mental: styles.coachDotMental,
};

export function CoachCard(props: CoachCardProps) {
  if (props.message) return <CoachCardMessage message={props.message} />;
  if (props.content) return <CoachCardLegacy content={props.content} />;
  return null;
}

function CoachCardMessage({ message }: { message: CoachMessage }) {
  const { coach, kicker, rightMeta, title, body, cta, trigger } = message;
  const dotClass = COACH_DOT_CLASS[coach];
  const display = COACH_DISPLAY[coach];
  // The kicker pill renders as "Label · Name" so the coach voice is
  // explicit even when the trigger label and coach voice diverge
  // (e.g., "Mention · Vaira" — trigger is mention, voice is race).
  const kickerLabel = kicker || `${display.label} · ${display.name}`;

  return (
    <section
      className={styles.coachCard}
      data-trigger={trigger.kind}
      data-coach={coach}
    >
      <div className={styles.kickerRow}>
        <span className={`${styles.coachDot} ${dotClass}`}>{kickerLabel}</span>
        {rightMeta && <span className={styles.kickerRight}>{rightMeta}</span>}
      </div>

      <h3 className={styles.title}>{title}</h3>

      <p className={styles.body} dangerouslySetInnerHTML={{ __html: sanitizeEm(body) }} />

      <div className={styles.footer} data-has-meta={rightMeta ? 'true' : 'false'}>
        <a className={styles.askLink} href={cta.href}>
          {cta.label} ›
        </a>
        {rightMeta && (
          <span className={styles.footerMeta}>read · {readingHint(body)}</span>
        )}
      </div>
    </section>
  );
}

/**
 * Allow exactly `<em>...</em>` in the body string. Strips any other
 * HTML tags so we never hand raw HTML from a rules-engine response to
 * `dangerouslySetInnerHTML` without a guard. Only `<em>` and `</em>`
 * are preserved — everything else is removed.
 */
function sanitizeEm(raw: string): string {
  // Remove all tags except <em> and </em>, then strip any attributes.
  return raw.replace(/<(?!\/?em\b)[^>]*>/gi, '').replace(/<em[^>]*>/gi, '<em>');
}

/**
 * Crude reading-hint label: "<N> sec" based on word count. Not strict
 * — design only needs a rough surface here. Caps at 60 sec.
 */
function readingHint(body: string): string {
  const words = body.replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length;
  // ~3 words per second.
  const secs = Math.max(5, Math.min(60, Math.round(words / 3)));
  return `${secs} sec`;
}

// ─── Legacy path (Phase 1 fixture) ─────────────────────────────────

function CoachCardLegacy({ content }: { content: CoachCardContent }) {
  return (
    <section className={styles.legacyCard} data-state={content.kicker}>
      <div className={styles.legacyKicker}>
        <span className={styles.legacyDot} style={{ background: content.kickerDot }} aria-hidden />
        <span>{content.kicker}</span>
        <span className={styles.legacyTime}>{content.time}</span>
      </div>
      <div
        className={`${styles.legacyQuote} serif`}
        style={{ '--em': content.emColor } as CSSProperties}
      >
        {content.quote}
      </div>
      <div className={styles.legacyActions}>
        {content.actions.map((a) => (
          <button
            key={a.label}
            className={[styles.legacyPill, a.primary ? '' : styles.legacyGhost]
              .filter(Boolean)
              .join(' ')}
          >
            {a.label}
          </button>
        ))}
      </div>
    </section>
  );
}
